import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Drawer,
  Empty,
  Input,
  List,
  Pagination,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FormOutlined,
  CopyOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Navigate, useNavigate } from "react-router-dom";

import StandardPage from "../components/Layout/StandardPage";
import { useAuth } from "../context/AuthContext";
import {
  deleteLandingPage,
  getLandingStats,
  getLandingPage,
  listLandingLeads,
  listLandingPages,
  createLandingPage,
  checkLandingSlugAvailability,
  type LandingLeadDTO,
  type LandingPageDTO,
  type LandingPageStats,
  type LandingPagePayload,
  createEmptyLandingDocument,
} from "../services/landingPages";
import { buildLandingPublicUrl } from "../utils/landingUrls";

const { Text, Title } = Typography;

const DEFAULT_PAGE_SIZE = 10;

type PaginationState = {
  current: number;
  pageSize: number;
  total: number;
};

const LandingPages: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = useMemo(
    () => String(user?.role || "").toUpperCase().replace(/^ROLE_/, ""),
    [user?.role],
  );
  const isAdmin = role === "ADMIN";

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<LandingPageDTO[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });

  const [leadDrawerOpen, setLeadDrawerOpen] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadList, setLeadList] = useState<LandingLeadDTO[]>([]);
  const [customerFilter, setCustomerFilter] = useState<string | null>(null);
  const [leadPagination, setLeadPagination] = useState<PaginationState>({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [leadTarget, setLeadTarget] = useState<LandingPageDTO | null>(null);
  const [drawerStats, setDrawerStats] = useState<LandingPageStats | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);

  const loadPages = useCallback(
    async (page = 1, size = DEFAULT_PAGE_SIZE) => {
      try {
        setLoading(true);
        const res = await listLandingPages({ page: page - 1, size });
        setData(res.content);
        setPagination({
          current: res.number + 1,
          pageSize: res.size,
          total: res.totalElements,
        });
      } catch (err) {
        const messageText = err instanceof Error ? err.message : "Errore durante il caricamento delle landing";
        message.error(messageText);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const loadLeads = useCallback(
    async (page: LandingPageDTO, pageNumber = 1, pageSize = 20) => {
      if (!page.id) return;
      try {
        setLeadLoading(true);
        const res = await listLandingLeads(page.id, {
          page: pageNumber - 1,
          size: pageSize,
        });
        setLeadList(res.content);
        setLeadPagination({
          current: res.number + 1,
          pageSize: res.size,
          total: res.totalElements,
        });
        const stats = await getLandingStats(page.id).catch(() => null);
        setDrawerStats(stats);
      } catch (err) {
        const messageText = err instanceof Error ? err.message : "Impossibile caricare i lead";
        message.error(messageText);
      } finally {
        setLeadLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (isAdmin) {
      void loadPages(1, DEFAULT_PAGE_SIZE);
    }
  }, [isAdmin, loadPages]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchTerm, customerFilter]);

  const customerOptions = useMemo(() => {
    const names = new Set<string>();
    let hasOrphan = false;
    data.forEach((item) => {
      if (item.customerName) {
        names.add(item.customerName);
      } else {
        hasOrphan = true;
      }
    });
    const opts = Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ label: name, value: name }));
    if (hasOrphan) {
      opts.unshift({ label: "Senza cliente", value: "__none__" });
    }
    return opts;
  }, [data]);

  const filteredData = useMemo(() => {
    const trimmed = searchTerm.trim().toLowerCase();
    return data.filter((item) => {
      const haystack = `${item.name ?? ""} ${item.slug ?? ""} ${item.description ?? ""} ${item.customerName ?? ""}`.toLowerCase();
      const matchesSearch = !trimmed || haystack.includes(trimmed);
      const matchesCustomer =
        !customerFilter ||
        (customerFilter === "__none__" ? !item.customerName : item.customerName === customerFilter);
      return matchesSearch && matchesCustomer;
    });
  }, [data, searchTerm, customerFilter]);

  const handleDelete = useCallback(
    async (page: LandingPageDTO) => {
      if (!page.id) return;
      try {
        await deleteLandingPage(page.id);
        message.success("Landing eliminata");
        void loadPages(pagination.current, pagination.pageSize);
      } catch (err) {
        const messageText = err instanceof Error ? err.message : "Errore durante l'eliminazione";
        message.error(messageText);
      }
    },
    [loadPages, pagination.current, pagination.pageSize],
  );

  const openLeadDrawer = useCallback(
    async (page: LandingPageDTO) => {
      setLeadTarget(page);
      setLeadDrawerOpen(true);
      setLeadList([]);
      setDrawerStats(null);
      await loadLeads(page, 1, leadPagination.pageSize);
    },
    [leadPagination.pageSize, loadLeads],
  );

  const openCreate = useCallback(() => {
    navigate("/landing-builder/new");
  }, [navigate]);


  const openEdit = useCallback(
    (page: LandingPageDTO) => {
      if (!page.id) {
        message.warning("ID landing non disponibile");
        return;
      }
      navigate(`/landing-builder/${page.id}`);
    },
    [navigate],
  );

  const findAvailableSlug = useCallback(async (base: string) => {
    const sanitizedBase = base
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
    let attempt = sanitizedBase || `landing-${Date.now()}`;
    for (let index = 0; index < 20; index += 1) {
      try {
        const availability = await checkLandingSlugAvailability(attempt);
        if (availability.available !== false) {
          return attempt;
        }
      } catch {
        // fallback to next attempt
      }
      attempt = `${sanitizedBase}-${index + 1}` || `landing-${Date.now()}`;
    }
    return `${sanitizedBase || "landing"}-${Date.now()}`;
  }, []);

  const duplicateLanding = useCallback(
    async (page: LandingPageDTO) => {
      if (!page.id) {
        message.warning("ID landing non disponibile");
        return;
      }
      setDuplicatingId(page.id);
      try {
        const source = await getLandingPage(page.id);
        const customerId = source.customerId ?? page.customerId;
        if (!customerId) {
          message.error("Associa un cliente prima di duplicare la landing.");
          return;
        }
        const sourceDocument = source.document ?? createEmptyLandingDocument();
        const defaultDocument = createEmptyLandingDocument();
        const document: LandingPagePayload["document"] = {
          ...defaultDocument,
          ...sourceDocument,
          blocks: Array.isArray(sourceDocument.blocks) ? sourceDocument.blocks : [],
          footer: sourceDocument.footer ?? defaultDocument.footer,
        };

        const baseName = source.name ? `${source.name} (copia)` : "Landing copiata";
        const baseSlug = source.slug ? `${source.slug}-copia` : `landing-${Date.now()}`;
        const slug = await findAvailableSlug(baseSlug);

        const payload: LandingPagePayload = {
          name: baseName,
          slug,
          description: source.description ?? null,
          customerId,
          backgroundColor: source.backgroundColor ?? null,
          backgroundImageUrl: source.backgroundImageUrl ?? null,
          backgroundOverlayColor: source.backgroundOverlayColor ?? null,
          backgroundOverlayOpacity: source.backgroundOverlayOpacity ?? null,
          logoUrl: source.logoUrl ?? null,
          primaryColor: source.primaryColor ?? null,
          buttonLabel: source.buttonLabel ?? null,
          successMessage: source.successMessage ?? null,
          eventDate: source.eventDate ?? null,
          reminderDate: source.reminderDate ?? null,
          reminderEnabled: source.reminderEnabled ?? false,
          reminderEmailSubject: source.reminderEmailSubject ?? null,
          reminderEmailBody: source.reminderEmailBody ?? null,
          couponEmailSubject: source.couponEmailSubject ?? null,
          couponEmailBody: source.couponEmailBody ?? null,
          emailSenderName: source.emailSenderName ?? null,
          document,
          published: false,
        };

        const created = await createLandingPage(payload);
        message.success("Landing duplicata");
        await loadPages(pagination.current, pagination.pageSize);
      } catch (err) {
        const messageText = err instanceof Error ? err.message : "Impossibile duplicare la landing";
        message.error(messageText);
      } finally {
        setDuplicatingId(null);
      }
    },
    [findAvailableSlug, loadPages, pagination.current, pagination.pageSize],
  );

  const columns = useMemo<ColumnsType<LandingPageDTO>>(
    () => [
      {
        title: "Nome",
        dataIndex: "name",
        key: "name",
        render: (value: string, record) => {
          const publicUrl = buildLandingPublicUrl(record.slug);
          return (
            <Space direction="vertical" size={0}>
              <Text strong>{value}</Text>
              <Text type="secondary">{publicUrl}</Text>
            </Space>
          );
        },
      },
      {
        title: "Cliente",
        dataIndex: "customerName",
        key: "customer",
        render: (_: string | undefined, record) =>
          record.customerName ? (
            <Text>{record.customerName}</Text>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: "Evento",
        key: "eventDate",
        render: (_: unknown, record) => {
          const eventLabel = record.eventDate
            ? dayjs(record.eventDate).format("DD/MM/YYYY HH:mm")
            : "Non impostata";
          const reminderEnabled = record.reminderEnabled ?? false;
          const reminderLabel =
            reminderEnabled && record.reminderDate
              ? dayjs(record.reminderDate).format("DD/MM/YYYY HH:mm")
              : null;
          return (
            <Space direction="vertical" size={0}>
              <Text>{eventLabel}</Text>
              {reminderEnabled ? (
                reminderLabel ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Reminder: {reminderLabel}
                  </Text>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Reminder: non programmato
                  </Text>
                )
              ) : null}
              {!reminderEnabled ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Reminder disattivato
                </Text>
              ) : null}
            </Space>
          );
        },
      },
      {
        title: "Stato",
        dataIndex: "published",
        key: "published",
        width: 120,
        render: (published: boolean) => (
          <Tag color={published ? "green" : "orange"}>
            {published ? "Pubblicata" : "Bozza"}
          </Tag>
        ),
      },
      {
        title: (
          <Tooltip title="Il tasso di conversione è calcolato come lead/visite.">
            Statistiche
          </Tooltip>
        ),
        key: "stats",
        width: 200,
        render: (_: unknown, record) => {
          const views = record.totalViews ?? 0;
          const submissions = record.totalSubmissions ?? 0;
          const conversion = views > 0 ? `${((submissions / views) * 100).toFixed(1)}%` : "0%";
          return (
            <Space direction="vertical" size={0}>
              <Text type="secondary">Visite: {views}</Text>
              <Text type="secondary">Lead: {submissions}</Text>
              <Text type="secondary">Coupon riscattati: {record.totalCouponRedeemed ?? 0}</Text>
              <Text type="secondary">Conversione: {conversion}</Text>
            </Space>
          );
        },
      },
      {
        title: "Ultima modifica",
        dataIndex: "updatedAt",
        key: "updatedAt",
        width: 200,
        render: (value?: string) =>
          value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "�",
      },
      {
        title: "Azioni",
        key: "actions",
        align: "right",
        render: (_: unknown, record) => (
          <Space>
            <Tooltip title="Visualizza lead raccolti">
              <Button icon={<FormOutlined />} onClick={() => openLeadDrawer(record)} />
            </Tooltip>
            <Tooltip title="Apri landing pubblica">
              <Button
                icon={<EyeOutlined />}
                onClick={() =>
                  window.open(buildLandingPublicUrl(record.slug), "_blank", "noopener,noreferrer")
                }
              />
            </Tooltip>
            <Tooltip title="Modifica">
              <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
            </Tooltip>
            <Tooltip title="Duplica">
              <Button
                icon={<CopyOutlined />}
                loading={duplicatingId === record.id}
                onClick={() => duplicateLanding(record)}
              />
            </Tooltip>
            <Popconfirm
              title={`Eliminare "${record.name}"?`}
              okText="Si"
              cancelText="No"
              onConfirm={() => handleDelete(record)}
            >
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [duplicateLanding, duplicatingId, handleDelete, openEdit, openLeadDrawer],
  );

  if (!isAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <StandardPage
      title="Landing Pages"
      subtitle="Crea e gestisci le pagine di acquisizione lead"
      actions={[
        {
          label: "Nuova landing",
          icon: <PlusOutlined />,
          type: "primary",
          onClick: openCreate,
        },
        {
          label: "Aggiorna",
          icon: <ReloadOutlined />,
          onClick: () => loadPages(pagination.current, pagination.pageSize),
        },
      ]}
      loading={loading && data.length === 0}
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Cerca per nome, slug o cliente"
          allowClear
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          onSearch={(value) => setSearchTerm(value)}
          style={{ width: 260 }}
        />
        <Select
          allowClear
          placeholder="Filtra per cliente"
          style={{ minWidth: 220 }}
          value={customerFilter ?? undefined}
          onChange={(value) => setCustomerFilter(value ?? null)}
          options={customerOptions}
        />
      </Space>

      <Table<LandingPageDTO>
        rowKey="id"
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        pagination={
          searchTerm || customerFilter
            ? false
            : {
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                onChange: async (pageNumber, size) => {
                  setPagination((prev) => ({ ...prev, current: pageNumber, pageSize: size }));
                  await loadPages(pageNumber, size);
                },
              }
        }
        locale={{
          emptyText: (
            <Empty
              description="Nessuna landing page creata"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />

      <Drawer
        title={
          <Space direction="vertical" size={0}>
            <Title level={4} style={{ marginBottom: 0 }}>
              Lead raccolti
            </Title>
            {leadTarget && (
              <Space direction="vertical" size={2}>
                <Text type="secondary">Landing: {leadTarget.name}</Text>
                <Text type="secondary">
                  {(() => {
                    const views = drawerStats?.views ?? leadTarget.totalViews ?? 0;
                    const submissions = drawerStats?.submissions ?? leadTarget.totalSubmissions ?? 0;
                    const redeemed = drawerStats?.couponRedeemed ?? leadTarget.totalCouponRedeemed ?? 0;
                    const conversion = views > 0 ? `${((submissions / views) * 100).toFixed(1)}%` : "0%";
                    return `Visite: ${views} · Lead: ${submissions} · Coupon riscattati: ${redeemed} · Conversione: ${conversion}`;
                  })()}
                </Text>
              </Space>
            )}
          </Space>
        }
        width={520}
        open={leadDrawerOpen}
        onClose={() => {
          setLeadDrawerOpen(false);
          setLeadTarget(null);
          setLeadList([]);
          setDrawerStats(null);
        }}
      >
        {leadLoading && leadList.length === 0 ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : leadList.length === 0 ? (
          <Empty description="Nessun lead raccolto" />
            ) : (
              <List
                itemLayout="vertical"
                dataSource={leadList}
                renderItem={(lead) => {
                  const nameParts = [lead.firstName, lead.lastName]
                    .map((part) => (part ?? "").trim())
                    .filter(Boolean);
                  const fullName = nameParts.join(" ");
                  const headerItems: React.ReactNode[] = [];
                  if (fullName) {
                    headerItems.push(
                      <Text strong key="name">
                        {fullName}
                      </Text>,
                    );
                  }
                  if (lead.email) {
                    headerItems.push(
                      <Text type="secondary" key="email">
                        {lead.email}
                      </Text>,
                    );
                  }
                  if (lead.phoneNumber) {
                    headerItems.push(
                      <Text type="secondary" key="phone">
                        {lead.phoneNumber}
                      </Text>,
                    );
                  }
                  if (headerItems.length === 0) {
                    headerItems.push(
                      <Text strong key="fallback">
                        Lead
                      </Text>,
                    );
                  }

                  return (
                    <List.Item key={lead.id}>
                      <List.Item.Meta
                        title={
                          <Space split={<span>-</span>} wrap>
                            {headerItems}
                            {lead.couponUsed ? <Tag color="green">Coupon riscattato</Tag> : null}
                          </Space>
                        }
                        description={
                          <Space direction="vertical" size={4}>
                            <Text type="secondary">
                              {lead.createdAt ? dayjs(lead.createdAt).format("DD/MM/YYYY HH:mm") : null}
                            </Text>
                            {lead.couponScanCount ? (
                              <Text type="secondary">Scan coupon: {lead.couponScanCount}</Text>
                            ) : null}
                          </Space>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            )}

        <div style={{ marginTop: 16 }}>
          <Pagination
            size="small"
            current={leadPagination.current}
            pageSize={leadPagination.pageSize}
            total={leadPagination.total}
            showSizeChanger
            onChange={async (pageNumber, size) => {
              if (!leadTarget) return;
              setLeadPagination((prev) => ({ ...prev, current: pageNumber, pageSize: size }));
              await loadLeads(leadTarget, pageNumber, size);
            }}
          />
        </div>
      </Drawer>
    </StandardPage>
  );
};

export default LandingPages;











